<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{ level: number; barCount?: number; threshold?: number }>(), {
  barCount: 20,
})

// Bars fill left-to-right on a perceptual (cube-root) curve, like the Slider's own volume
// mapping elsewhere — so quiet speech still visibly moves a few bars instead of the whole
// meter living in the bottom 10% of a linear scale.
const activeBars = computed(() => Math.round(Math.cbrt(props.level) * props.barCount))
// The threshold on the same curve, so the mark sits where the bars cross it.
const thresholdPosition = computed(() =>
  props.threshold === undefined ? null : `${Math.min(100, Math.cbrt(props.threshold) * 100)}%`
)

function barClass(index: number) {
  if (index >= activeBars.value) return 'bg-muted'
  const ratio = index / props.barCount
  if (ratio > 0.85) return 'bg-red-500'
  if (ratio > 0.65) return 'bg-yellow-500'
  return 'bg-green-500'
}
</script>

<template>
  <div class="relative flex w-full items-center justify-between gap-[3px]" role="meter" :aria-valuenow="Math.round(level * 100)" aria-valuemin="0" aria-valuemax="100">
    <div
      v-for="i in barCount"
      :key="i"
      class="h-3 w-full rounded-sm transition-colors duration-75"
      :class="barClass(i - 1)"
    />
    <div
      v-if="thresholdPosition"
      class="pointer-events-none absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-full bg-foreground"
      :style="{ left: thresholdPosition }"
      aria-hidden="true"
    />
  </div>
</template>
